import {
  consoleEvents,
  trackEvent,
  trackESalesEvent,
  trackViewEvent,
  waitUntil,
  getLocale,
  getTouchpoint,
  isHmBrand,
} from '@pb/utils'
import { pathOr } from 'ramda'

import { TRACKING_EVENTS } from 'App/constants'
import appActions from 'App/duck/actions'
import { TrackingEventData } from 'App/duck/types/tracking'

const hm = window.hm || {}

export const CLICKTALE_SIGNALS = {
  start: 'START',
  end: 'END',
}

type CustomTrackerEvent = (
  trackingEventId: string,
  clickTaleSignal?: string,
  isAsync?: boolean,
) => (add?: Record<string, unknown>) => void // add anything to the tracking object

type CustomTrackers = {
  trackEvent: CustomTrackerEvent
  trackViewEvent: CustomTrackerEvent
}

interface EventTypes {
  event_category: string
  event_action: string
  event_label: string
  event_type: string
  error_id: string
  error_label: string
  error_type: string
  error_category: string
}

interface BrandTrackingEventData extends EventTypes {
  page_id: string
  page_type: string
  category_id: string
  category_path: string
  region_locale: string
  region_market: string
  customer_city: string
  customer_country: string
  customer_state: string
  customer_zip: string
  session_touchpoint: string
}

export const setABTestingData = (data): any => {
  hm.trackingData = {
    ...data,
  }
}

export const getBrandTrackEventData = (
  {
    /* eslint-disable @typescript-eslint/naming-convention */
    page_id,
    category_id,
    category_path,
    selected_market: region_market,
    customer_city,
    customer_country,
    customer_state,
    customer_zip,
    event_category,
    event_id: event_action,
    event_label,
    event_type,
  }: Partial<TrackingEventData>,
  eventType?: 'error',
): Partial<BrandTrackingEventData> => {
  const events =
    eventType === 'error'
      ? {
          error_id: event_action,
          error_label: event_label,
          error_type: event_type,
          error_category: event_category,
        }
      : { event_action, event_label, event_type, event_category }

  return {
    page_id,
    category_id,
    page_type: category_id,
    category_path,
    region_market,
    region_locale: getLocale('mixed') || '',
    customer_city,
    customer_country,
    customer_state,
    customer_zip,
    session_touchpoint: getTouchpoint() || '',
    ...events,
  }
}

const removeNullOrUndefined = (obj: object): object =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value != null))

export const trackEventAdapter = (data: Partial<TrackingEventData>, eventType?: 'error'): void => {
  if (isHmBrand) {
    trackEvent(data)
  } else {
    trackEvent(removeNullOrUndefined(getBrandTrackEventData(data, eventType)), false, false)
  }
}

export const trackViewEventAdapter = (data: Partial<TrackingEventData>): void => {
  if (isHmBrand) {
    trackViewEvent(data)
  } else {
    trackViewEvent(removeNullOrUndefined(getBrandTrackEventData(data)), false, false)
  }
}

export const appTracking = ({ type, payload }): any => {
  const { id, ...customTrackingData } = payload

  if (type === appActions.trackESalesEvent.getType()) {
    const { url, ...data } = customTrackingData
    trackESalesEvent({ url, data })
    return
  }

  if (!id) {
    consoleEvents.error('appTracking :: missing expected property id')
    return
  }

  const trackingData = { ...TRACKING_EVENTS[id].data, ...customTrackingData }

  if (type === appActions.trackEvent.getType()) {
    trackEventAdapter(trackingData)
  }
}

export const buildCustomTrackers = ({ staticDataMap, persistentData }): CustomTrackers => {
  const buildTrackingData = ({
    trackingEventId,
    customData,
  }: {
    trackingEventId: string
    customData?: Record<string, unknown>
  }): Record<string, unknown> => {
    const staticData = pathOr({}, [trackingEventId, 'data'])(staticDataMap)
    return {
      ...staticData,
      ...persistentData,
      ...customData,
    }
  }

  const clickTaleSignalStart = (eventType: string | null = null): void => {
    if (typeof window.clickTaleStartEventSignal === 'function') {
      if (eventType) {
        window.clickTaleStartEventSignal(`Onload | ${eventType}`)
      } else {
        window.clickTaleStartEventSignal()
      }
    }
  }

  const clickTaleSignalEnd = (): void => {
    if (typeof (window as any).clickTaleEndEventSignal === 'function') {
      window.clickTaleEndEventSignal()
    }
  }

  const getIsClickTaleAvailable = (): boolean => window.clickTaleTagInjected

  const trackClickTaleEvent = (
    trackingEventId: string | undefined | null = null,
    clickTaleSignal: string,
  ): void => {
    waitUntil(getIsClickTaleAvailable)
      .then(() => {
        if (clickTaleSignal === CLICKTALE_SIGNALS.start) {
          clickTaleSignalStart(trackingEventId)
        }
        if (clickTaleSignal === CLICKTALE_SIGNALS.end) {
          clickTaleSignalEnd()
        }
      })
      .catch((error) => consoleEvents.error('clickTale is not available', error))
  }

  const customEventTracker: CustomTrackerEvent = (
    trackingEventId,
    clickTaleSignal,
    isAsync = true,
  ) => {
    return (customData) => {
      let trackingData
      if (customData) {
        const staticTrackingData = buildTrackingData({ trackingEventId })
        const doesCustomDataHaveDEP =
          !!customData?.dep_variable_name && !!customData?.dep_variable_format
        const doesStaticTrackingDataHaveDEP =
          !!staticTrackingData?.dep_variable_name && !!staticTrackingData?.dep_variable_format
        if (doesCustomDataHaveDEP && doesStaticTrackingDataHaveDEP) {
          const newCustomData = { ...customData }
          newCustomData.dep_variable_name = [
            ...(staticTrackingData.dep_variable_name as string[]),
            ...(newCustomData.dep_variable_name as string[]),
          ]
          newCustomData.dep_variable_format = [
            ...(staticTrackingData.dep_variable_format as string[]),
            ...(newCustomData.dep_variable_format as string[]),
          ]
          trackingData = buildTrackingData({ trackingEventId, customData: newCustomData })
        } else {
          trackingData = buildTrackingData({ trackingEventId, customData })
        }
      } else {
        trackingData = buildTrackingData({ trackingEventId })
      }

      if (clickTaleSignal) {
        trackClickTaleEvent(trackingData?.event_type, clickTaleSignal)
      }
      trackEvent(trackingData, isAsync)
    }
  }
  const customViewEventTracker: CustomTrackerEvent = (
    trackingEventId,
    clickTaleSignal,
    isAsync = true,
  ) => {
    return (customData) => {
      const trackingData = buildTrackingData({ trackingEventId, customData })
      if (clickTaleSignal) {
        trackClickTaleEvent(trackingData?.event_type as string | undefined, clickTaleSignal)
      }

      trackViewEvent(trackingData, isAsync)
    }
  }

  return {
    trackEvent: customEventTracker,
    trackViewEvent: customViewEventTracker,
  }
}
